<template>
  <section
    class="return-voucher-wrapper"
    aria-label="Détail du bon de retour logistique"
    role="main"
  >
    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="returnVoucher === undefined"
      class="modern-alert d-print-none"
      role="alert"
      aria-live="assertive"
      aria-label="Aucun bon de retour trouvé"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          icon="AlertCircleIcon"
          size="24"
          class="mr-2"
          aria-hidden="true"
        />
        <div>
          <h4 class="alert-heading mb-1">
            Erreur lors du chargement
          </h4>
          <div class="alert-body">
            Aucun bon de retour trouvé avec cet ID. Consultez la
            <b-link
              class="alert-link"
              :to="{ name: 'return-vouchers-list' }"
              aria-label="Retour à la liste des bons de retour logistiques"
            >
              liste des retours
            </b-link>
            pour d'autres bons.
          </div>
        </div>
      </div>
    </b-alert>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="loading-container d-print-none"
      aria-busy="true"
      aria-live="polite"
    >
      <b-spinner
        variant="primary"
        label="Chargement du bon de retour logistique..."
        class="loading-spinner"
        aria-label="Chargement du bon de retour logistique..."
      />
      <p class="loading-text mt-3">
        Chargement du bon de retour logistique...
      </p>
    </div>

    <!-- Main Content -->
    <div
      v-if="!isLoading && returnVoucher"
      aria-label="Contenu principal du bon de retour logistique"
      role="region"
    >
      <!-- Top Action Bar -->
      <div class="top-action-bar mb-4 d-print-none">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            md="6"
          >
            <h2 class="page-title mb-0">
              <feather-icon
                icon="PackageIcon"
                size="24"
                class="mr-2"
              />
              Bon de retour #{{ returnVoucher.id }}
            </h2>
            <p class="page-subtitle">
              Créé le {{ formatFn(returnVoucher.created_at) }}
            </p>
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="text-md-right"
          >
            <div class="action-buttons-top">
              <div class="view-mode-toggle mr-3">
                <span class="view-mode-label">Aperçu</span>
                <b-button-group size="sm">
                  <b-button
                    :variant="visualMode === 'color' ? 'danger' : 'outline-secondary'"
                    @click="setVisualMode('color')"
                  >
                    <feather-icon
                      icon="ApertureIcon"
                      size="14"
                      class="mr-1"
                    />
                    Couleur
                  </b-button>
                  <b-button
                    :variant="visualMode === 'bw' ? 'dark' : 'outline-secondary'"
                    @click="setVisualMode('bw')"
                  >
                    <feather-icon
                      icon="PrinterIcon"
                      size="14"
                      class="mr-1"
                    />
                    N&B
                  </b-button>
                </b-button-group>
              </div>
              <b-dropdown
                v-ripple.400="'rgba(10, 38, 52, 0.15)'"
                variant="outline-danger"
                class="action-btn-top"
                :disabled="isLoading"
                text="Imprimer"
                right
              >
                <template #button-content>
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Imprimer
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="14"
                    class="ml-1"
                  />
                </template>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer une version simplifiée en noir et blanc, sans la colonne Motifs de retour.'"
                  @click="printVoucherBW"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version noir et blanc
                </b-dropdown-item>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer le bon de retour en couleur avec tous les éléments graphiques.'"
                  @click="printVoucher"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version couleur
                </b-dropdown-item>
              </b-dropdown>

              <!-- Quick Stats -->
              <div class="quick-stats d-inline-flex ml-3">
                <div class="stat-item-inline info">
                  <feather-icon
                    icon="PackageIcon"
                    size="14"
                  />
                  <span>{{ returnVoucher.orders_count }}</span>
                </div>
                <div class="stat-item-inline danger">
                  <feather-icon
                    icon="DollarSignIcon"
                    size="14"
                  />
                  <span>{{ returnVoucher.total_return_price }} DT</span>
                </div>
                <div class="stat-item-inline primary">
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="14"
                  />
                  <span>{{ returnVoucher.total_price }} DT</span>
                </div>
                <div :class="['stat-item-inline', returnVoucher.is_validated ? 'success' : 'warning']">
                  <feather-icon
                    :icon="returnVoucher.is_validated ? 'CheckCircleIcon' : 'ClockIcon'"
                    size="14"
                  />
                  <span>{{ returnVoucher.is_validated ? 'Validé' : 'En attente' }}</span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Voucher Content - This is what gets printed -->
      <div
        id="voucher-content"
        class="voucher-content"
      >
        <!-- Full Width Voucher Document -->
        <b-card :class="['voucher-card shadow-sm', voucherCardClasses]">
          <!-- Header Section -->
          <div class="voucher-header">
            <b-row class="align-items-center">
              <b-col
                cols="12"
                md="6"
                class="mb-3 mb-md-0"
              >
                <div class="company-logo">
                  <img
                    :src="logoSrc"
                    :alt="$branding.logo.alt"
                    class="logo-img"
                  >
                </div>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="text-md-right"
              >
                <h1 class="voucher-title">
                  Bon de retour N°
                  <span class="voucher-number">{{ returnVoucher.id }}</span>
                </h1>
                <div class="voucher-date">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="mr-1"
                  />
                  {{ formatFn(returnVoucher.created_at) }}
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Company Information -->
          <b-row class="company-info-section">
            <b-col cols="12">
              <document-company-info
                :company="company"
                header-title="Informations de la société"
                custom-tagline="Service de retour des colis"
              >
                <template #icon>
                  <feather-icon
                    icon="BuildingIcon"
                    size="20"
                    class="mr-2"
                  />
                </template>
              </document-company-info>
            </b-col>
          </b-row>

          <!-- Summary Table -->
          <div class="summary-section">
            <h5 class="section-title">
              <feather-icon
                icon="BarChartIcon"
                size="20"
                class="mr-2"
              />
              Résumé du bon de retour
            </h5>
            <div class="summary-table-container">
              <div class="table-responsive">
                <table class="modern-table">
                  <thead>
                    <tr>
                      <th>Nombre de colis</th>
                      <th>Date de création</th>
                      <th>Statut de validation</th>
                      <th>Date de validation</th>
                      <th>Total TTC</th>
                      <th>Total frais de retour</th>
                      <th>Net à retourner</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <b-badge
                          variant="info"
                          class="stat-badge"
                        >
                          {{ returnVoucher.orders_count }}
                        </b-badge>
                      </td>
                      <td>{{ formatFn(returnVoucher.created_at) }}</td>
                      <td>
                        <b-badge :variant="returnVoucher.is_validated ? 'success' : 'warning'">
                          {{ returnVoucher.is_validated ? 'Validé' : 'En attente' }}
                        </b-badge>
                      </td>
                      <td>
                        <span v-if="returnVoucher.validated_at">
                          {{ formatFn(returnVoucher.validated_at) }}
                        </span>
                        <span
                          v-else
                          class="text-muted"
                        >-</span>
                      </td>
                      <td class="amount">
                        {{ returnVoucher.total_price }} DT
                      </td>
                      <td class="amount danger">
                        {{ returnVoucher.total_return_price }} DT
                      </td>
                      <td class="amount total-amount">
                        {{ netToReturn }} DT
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Orders Table -->
          <div class="orders-section">
            <div class="section-header">
              <h5 class="section-title">
                <feather-icon
                  icon="PackageIcon"
                  size="20"
                  class="mr-2"
                />
                Détails des colis retournés
                <b-badge
                  variant="light-primary"
                  class="ml-2"
                >
                  {{ returnVoucher.orders_count }} colis
                </b-badge>
              </h5>
            </div>

            <!-- Supplier Info Section (above table) -->
            <div class="supplier-info-section mb-3">
              <b-card class="info-card">
                <div class="info-header">
                  <feather-icon
                    icon="UserIcon"
                    size="20"
                    class="mr-2"
                  />
                  <h5 class="mb-0">
                    Fournisseur
                  </h5>
                </div>
                <div class="info-content">
                  <div class="info-item">
                    <span class="info-label">Nom:</span>
                    <span>{{ returnVoucher.supplier && returnVoucher.supplier.username ? returnVoucher.supplier.username : '-' }}</span>
                  </div>
                  <div
                    v-if="returnVoucher.supplier && returnVoucher.supplier.email"
                    class="info-item"
                  >
                    <span class="info-label">Email:</span>
                    <span>{{ returnVoucher.supplier.email }}</span>
                  </div>
                  <div
                    v-if="returnVoucher.supplier && returnVoucher.supplier.phone"
                    class="info-item"
                  >
                    <span class="info-label">Téléphone:</span>
                    <span>{{ returnVoucher.supplier.phone }}</span>
                  </div>
                </div>
              </b-card>
            </div>

            <div class="table-container">
              <b-table-lite
                responsive
                class="modern-orders-table"
                :items="orders"
                :fields="[
                  { key: 'BL', label: 'N° Colis' },
                  { key: 'zone_adresse', label: 'Adresse de livraison' },
                  { key: 'tel', label: 'Téléphone' },
                  { key: 'destinataire', label: 'Destinataire' },
                  { key: 'prix', label: 'Prix TTC' },
                  { key: 'prix_de_retour', label: 'Frais retour' },
                  { key: 'motifs_retour', label: 'Motifs de retour', thClass: 'motifs-column', tdClass: 'motifs-column' }
                ]"
                striped
                hover
              >
                <!-- Order ID Column -->
                <template #cell(BL)="data">
                  <div class="order-id-cell">
                    <strong class="order-number">#{{ data.item.BL }}</strong>
                    <b-badge
                      v-if="data.item.is_an_exchange"
                      variant="warning"
                      class="exchange-badge"
                    >
                      Échange
                    </b-badge>
                  </div>
                </template>

                <!-- Address Column -->
                <template #cell(zone_adresse)="data">
                  <div class="address-cell">
                    <div class="address-item">
                      <feather-icon
                        icon="MapPinIcon"
                        size="14"
                        class="mr-1"
                      />
                      <strong>{{ data.item.zone_adresse.governorate }}</strong>
                    </div>
                    <div class="address-item">
                      <span class="text-muted">{{ data.item.zone_adresse.delegation }}</span>
                    </div>
                    <div class="address-item">
                      <span class="text-muted">{{ data.item.zone_adresse.locality }}</span>
                    </div>
                    <div class="address-item">
                      <small class="text-muted">{{ data.item.zone_adresse.address }}</small>
                    </div>
                  </div>
                </template>

                <!-- Phone Column -->
                <template #cell(tel)="data">
                  <div class="phone-cell">
                    <div class="phone-item">
                      <feather-icon
                        icon="PhoneIcon"
                        size="14"
                        class="mr-1"
                      />
                      {{ data.item.tel }}
                    </div>
                    <div
                      v-if="data.item.tel2"
                      class="phone-item"
                    >
                      <feather-icon
                        icon="PhoneIcon"
                        size="14"
                        class="mr-1"
                      />
                      {{ data.item.tel2 }}
                    </div>
                  </div>
                </template>

                <!-- Customer Column -->
                <template #cell(destinataire)="data">
                  <div class="customer-cell">
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                      class="mr-2 text-primary"
                    />
                    <span class="customer-name">{{ data.item.destinataire }}</span>
                  </div>
                </template>

                <!-- Price Column -->
                <template #cell(prix)="data">
                  <div class="price-cell">
                    <span class="price-amount">{{ data.item.prix }} DT</span>
                  </div>
                </template>

                <!-- Return Price Column -->
                <template #cell(prix_de_retour)="data">
                  <div class="return-price-cell">
                    <span class="return-price-amount">{{ data.item.prix_de_retour }} DT</span>
                  </div>
                </template>

                <!-- Return Reasons Column -->
                <template #cell(motifs_retour)="data">
                  <div class="return-reasons-cell">
                    <div
                      v-if="data.item.return_reasons && data.item.return_reasons.length"
                      class="reasons-list"
                    >
                      <div
                        v-for="(reason, idx) in data.item.return_reasons"
                        :key="idx"
                        class="reason-item"
                      >
                        <div class="reason-main">
                          <div class="reason-text">
                            <strong class="reason-title">{{ reason.return_reason }}</strong>
                            <div
                              v-if="reason.additional_note"
                              class="reason-note"
                            >
                              <feather-icon
                                icon="MessageSquareIcon"
                                size="12"
                                class="mr-1"
                              />
                              {{ reason.additional_note }}
                            </div>
                          </div>

                          <div class="reason-meta">
                            <div
                              v-if="reason.created_at"
                              class="reason-date"
                            >
                              <feather-icon
                                icon="ClockIcon"
                                size="12"
                                class="mr-1"
                              />
                              <span class="date-label">Signalé:</span>
                              <span class="date-value">{{ formatDateOnly(reason.created_at) }}</span>
                            </div>
                            <div
                              v-if="reason.scheduled_date"
                              class="reason-date scheduled"
                            >
                              <feather-icon
                                icon="CalendarIcon"
                                size="12"
                                class="mr-1"
                              />
                              <span class="date-label">Planifié:</span>
                              <span class="date-value">{{ formatDateOnly(reason.scheduled_date) }}</span>
                            </div>
                          </div>

                          <div
                            v-if="reason.quick_options && reason.quick_options.length"
                            class="reason-options"
                          >
                            <div class="options-label">
                              Options:
                            </div>
                            <div class="options-list">
                              <b-badge
                                v-for="(option, optionIdx) in reason.quick_options"
                                :key="optionIdx"
                                variant="outline-secondary"
                                class="option-badge"
                              >
                                {{ option }}
                              </b-badge>
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="idx < data.item.return_reasons.length - 1"
                          class="reason-divider"
                        />
                      </div>
                    </div>
                    <div
                      v-else
                      class="no-reason"
                    >
                      <feather-icon
                        icon="MinusIcon"
                        size="14"
                        class="mr-1 text-muted"
                      />
                      <span class="text-muted">Aucun motif spécifié</span>
                    </div>
                  </div>
                </template>
              </b-table-lite>
            </div>
          </div>

          <!-- Signature Section -->
          <document-signature
            left-label="Signature du responsable"
            :company="company"
          />

          <!-- Totals Footer -->
          <div class="totals-footer mt-4">
            <div class="totals-grid">
              <div class="total-item">
                <span class="total-label">Total TTC :</span>
                <span class="total-value primary">{{ returnVoucher.total_price }} DT</span>
              </div>
              <div class="total-item">
                <span class="total-label">Total frais de retour :</span>
                <span class="total-value danger">{{ returnVoucher.total_return_price }} DT</span>
              </div>
              <div class="total-item highlight">
                <span class="total-label">Net à retourner :</span>
                <span class="total-value">{{ netToReturn }} DT</span>
              </div>
            </div>
          </div>
        </b-card>
      </div>
    </div>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTableLite,
  BAlert,
  BLink,
  BBadge,
  BSpinner,
  BDropdown,
  BDropdownItem,
  BButton,
  BButtonGroup,
  VBToggle,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import { DocumentCompanyInfo, DocumentSignature } from '@/components/document'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BTableLite,
    BAlert,
    BLink,
    BBadge,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BButton,
    BButtonGroup,
    DocumentCompanyInfo,
    DocumentSignature,
  },
  data() {
    return {
      returnVoucher: [],
      orders: [],
      company: {},
      isLoading: false,
      visualMode: 'color', // 'color' or 'bw'
      logos: {
        color: '/android-chrome-512x512.png',
        bw: '/android-chrome-512x512.png',
      },
    }
  },
  computed: {
    netToReturn() {
      if (!this.returnVoucher || !this.returnVoucher.total_price) {
        return '0.00'
      }
      return (this.returnVoucher.total_price - this.returnVoucher.total_return_price).toFixed(2)
    },
    voucherCardClasses() {
      return {
        'voucher-card--bw': this.visualMode === 'bw',
        'voucher-card--color': this.visualMode === 'color',
      }
    },
    logoSrc() {
      return this.visualMode === 'bw' ? this.logos.bw : this.logos.color
    },
  },
  created() {
    this.company = storeAuth.state.company
    this.id = this.$route.params.id
    this.getReturnVoucher()
  },
  methods: {
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    formatDateOnly(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      return `${day1.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    },
    async getReturnVoucher() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/return-vouchers/detail/${this.$route.params.id}`)
        this.returnVoucher = data
        this.orders = []
        const { orders } = this.returnVoucher
        for (let i = 0; i < orders.length; i += 1) {
          const order = orders[i]
          const dataObj = {
            code: order.id,
            gouvernorat: order.governorate,
            // fournisseur: order.supplier.username, // removed, now shown above table
            tel: order.phone1,
            tel2: order.phone2 || '',
            destinataire: order.name,
            adresse: order.address,
            prix: order.price_ttc,
            prix_de_retour: order.price_return,
            is_an_exchange: order.is_an_exchange,
            return_reasons: order.all_return_reasons || order.return_reasons || [],
            BL: order.id,
            zone_adresse: {
              governorate: order.governorate,
              delegation: order.delegation,
              locality: order.locality || order.locality,
              address: order.address,
            },
          }
          this.orders.push(dataObj)
        }
      } catch (e) {
        // handle error if needed
      }
      this.isLoading = false
    },
    printVoucher() {
      const previousMode = this.visualMode
      const originalTitle = document.title
      const faviconEl = document.querySelector("link[rel~='icon'], link[rel='shortcut icon']")
      const originalFaviconHref = faviconEl ? faviconEl.getAttribute('href') : null
      if (faviconEl) {
        faviconEl.setAttribute('href', "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'></svg>")
      }
      document.title = ''
      this.visualMode = 'color'

      this.$nextTick(() => {
        // Improved print CSS for correct page-break handling
        const printCSS = `
        <style id="print-styles">
          @page {
            margin: 5mm;
            padding: 0;
            size: auto;
          }
          @media print {
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              height: auto !important;
              background-color: white !important;
              font-family: Arial, sans-serif;
              font-size: 12px;
            }
            body * { visibility: hidden; }
            #voucher-content, #voucher-content * { visibility: visible; }
            
            #voucher-content { 
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              margin: 0 !important;
              padding: 5px !important;
              font-family: Arial, sans-serif;
              font-size: 12px;
              color: #000;
            }
            
            /* Hide UI and non-essential elements */
            .top-action-bar, .d-print-none, .modern-alert, .feather-icon {
               display: none !important;
            }

            /* Hide company info section - already have logo in header */
            .company-info-section {
              display: none !important;
            }

            /* Reset Wrapper */
            .return-voucher-wrapper {
              display: block !important;
              padding: 0 !important;
              margin: 0 !important;
              background: transparent !important;
              min-height: auto !important;
            }

            /* Card Reset */
            .voucher-card {
              border: none !important;
              box-shadow: none !important;
              margin: 0 !important;
              padding: 0 !important;
              background: white !important;
              border-radius: 0 !important;
            }
            .voucher-card .card-body {
              padding: 5px !important;
            }

            /* COMPACT HEADER - Single row with logo left, title right */
            .voucher-header {
              display: flex !important;
              flex-direction: row !important;
              align-items: center !important;
              justify-content: space-between !important;
              margin: 0 0 10px 0 !important;
              padding: 8px 12px !important;
              background: linear-gradient(135deg, rgba(234, 84, 85, 0.08) 0%, rgba(234, 84, 85, 0.04) 100%) !important;
              border-left: 4px solid #ea5455 !important;
              border-radius: 0 !important;
              width: 100% !important;
            }

            .voucher-header .row {
              display: flex !important;
              flex-direction: row !important;
              width: 100% !important;
              margin: 0 !important;
              align-items: center !important;
            }
            
            .voucher-header .row > [class*="col"] {
              flex: 0 0 auto !important;
              width: auto !important;
              max-width: none !important;
              padding: 0 !important;
              margin: 0 !important;
            }

            .voucher-header .row > .col-md-6:first-child {
              flex: 0 0 auto !important;
            }
            
            .voucher-header .row > .col-md-6:last-child {
              flex: 1 !important;
              text-align: right !important;
            }

            /* Single Logo - compact */
            .company-logo {
              padding: 0 !important;
              margin: 0 !important;
              background: transparent !important;
            }
            .logo-img {
              max-width: 120px !important;
              max-height: 40px !important;
              width: auto !important;
              height: auto !important;
              display: block !important;
              object-fit: contain !important;
            }

            /* Title styling - compact */
            .voucher-title {
              font-size: 16px !important;
              font-weight: bold !important;
              margin: 0 !important;
              color: #1a1a1a !important;
              line-height: 1.2 !important;
            }
            .voucher-number {
              color: #b0151a !important;
            }
            .voucher-date {
              font-size: 11px !important;
              color: #666 !important;
              margin: 2px 0 0 0 !important;
            }

            /* Summary section - compact */
            .summary-section {
              margin-bottom: 8px !important;
            }
            .section-title {
              font-size: 12px !important;
              font-weight: bold !important;
              margin: 0 0 5px 0 !important;
              padding-bottom: 3px !important;
              border-bottom: 1px solid #ddd !important;
              color: #333 !important;
            }
            .summary-table-container {
              margin-bottom: 8px !important;
              border: 1px solid #ddd !important;
              border-radius: 4px !important;
              overflow: hidden !important;
            }

            /* Tables - readable but compact */
            .modern-table, .modern-orders-table {
              font-size: 10px !important;
              width: 100% !important;
              border-collapse: collapse !important;
            }
            .modern-table th {
              background: #0A2634 !important;
              color: white !important;
              font-size: 10px !important;
              padding: 5px 6px !important;
              text-align: center !important;
              border: 1px solid #0A2634 !important;
            }
            .modern-table td {
              font-size: 10px !important;
              padding: 4px 6px !important;
              text-align: center !important;
              border: 1px solid #ddd !important;
            }
            .modern-orders-table th {
              background: #f5f5f5 !important;
              color: #333 !important;
              font-size: 9px !important;
              padding: 5px 4px !important;
              border: 1px solid #ddd !important;
            }
            .modern-orders-table td {
              font-size: 9px !important;
              padding: 4px !important;
              border: 1px solid #ddd !important;
            }

            /* Orders section */
            .orders-section {
              margin-bottom: 8px !important;
            }
            .section-header {
              margin-bottom: 5px !important;
            }
            .supplier-info-section {
              margin-bottom: 5px !important;
            }
            .info-card {
              padding: 6px !important;
              margin-bottom: 5px !important;
              border: 1px solid #ddd !important;
              border-left: 3px solid #ea5455 !important;
              background: #fafafa !important;
            }
            .info-header {
              font-size: 11px !important;
              margin-bottom: 4px !important;
              padding-bottom: 3px !important;
              border-bottom: 1px solid #eee !important;
            }
            .info-item {
              font-size: 10px !important;
              margin-bottom: 2px !important;
            }

            /* Totals - compact */
            .totals-footer {
              margin-top: 8px !important;
              padding: 8px !important;
              background: #f8f9fa !important;
              border: 1px solid #ddd !important;
              border-radius: 4px !important;
            }
            .totals-grid {
              display: flex !important;
              justify-content: space-around !important;
              gap: 10px !important;
            }
            .total-item {
              text-align: center !important;
              padding: 5px 10px !important;
              background: white !important;
              border-radius: 4px !important;
              border: 1px solid #eee !important;
            }
            .total-item.highlight {
              background: #0A2634 !important;
              color: white !important;
            }
            .total-label {
              font-size: 9px !important;
              font-weight: 600 !important;
            }
            .total-value {
              font-size: 12px !important;
              font-weight: bold !important;
            }
            .total-value.primary, .total-value.danger {
              color: #0A2634 !important;
            }
            .highlight .total-label, .highlight .total-value {
              color: white !important;
            }

            /* Signature - minimal */
            .signature-section {
              margin-top: 10px !important;
              page-break-inside: avoid !important;
            }

            /* Page breaks */
            tr { page-break-inside: avoid !important; }
            .summary-section, .totals-footer {
              page-break-inside: avoid !important;
            }
          }
        </style>
      `

        document.head.insertAdjacentHTML('beforeend', printCSS)
        window.print()
        setTimeout(() => {
          const printStyleElement = document.getElementById('print-styles')
          if (printStyleElement) {
            printStyleElement.remove()
          }
          document.title = originalTitle
          if (faviconEl && originalFaviconHref) {
            faviconEl.setAttribute('href', originalFaviconHref)
          }
          this.visualMode = previousMode
        }, 1000)
      })
    },
    printVoucherBW() {
      const previousMode = this.visualMode
      const originalTitle = document.title
      const faviconEl = document.querySelector("link[rel~='icon'], link[rel='shortcut icon']")
      const originalFaviconHref = faviconEl ? faviconEl.getAttribute('href') : null
      if (faviconEl) {
        faviconEl.setAttribute('href', "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'></svg>")
      }
      document.title = ''
      this.visualMode = 'bw'

      this.$nextTick(() => {
        // Ultra-compact B&W print CSS for maximum paper efficiency
        const printCSS = `
        <style id="print-styles-bw">
          @page {
            margin: 0;
            padding: 0;
          }
          @media print {
            html, body {
              margin: 0 !important;
              padding: 0 !important;
              font-family: Arial, sans-serif;
              font-size: 11px;
              line-height: 1.3;
              color: #000;
              background: #fff !important;
              height: auto !important;
            }
            body * { visibility: hidden; }
            #voucher-content, #voucher-content * { visibility: visible; }
            .return-voucher-wrapper {
              min-height: auto !important;
              height: auto !important;
              padding: 0 !important;
            }
            main, section, #app {
              margin: 0 !important;
              padding: 0 !important;
            }
            #voucher-content {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              font-size: 11px;
              color: #000;
              background: #fff !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .voucher-card {
              margin: 0 !important;
              padding: 10px !important;
            }
            .d-print-none, .feather-icon, .no-print { display: none !important; }
            .motifs-column { display: none !important; }
            .voucher-header {
              margin-bottom: 1px;
              padding: 2px;
              background: #fff !important;
              color: #000 !important;
              border: 1px solid #000;
              page-break-inside: avoid;
            }
            .logo-img {
              max-width: 100px !important;
              height: auto !important;
              display: none !important;
              filter: none !important;
              -webkit-filter: none !important;
              margin-bottom: 0;
              opacity: 1 !important;
            }
            .voucher-title {
              font-size: 9px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 1px;
            }
            .voucher-number, .voucher-date {
              color: #000 !important;
              font-size: 11px;
            }
            .section-title {
              font-size: 12px;
              font-weight: bold;
              border-bottom: 1px solid #000;
              margin: 10px 0 5px 0;
              padding-bottom: 2px;
              color: #000 !important;
              page-break-after: avoid;
            }
            .row { display: flex; flex-wrap: wrap; margin: 0; }
            .col-md-6 { flex: 0 0 50%; max-width: 50%; padding: 0 1px; }
            .info-card {
              border: 1px solid #000;
              padding: 2px;
              margin-bottom: 1px;
              background: #fff !important;
              min-height: auto;
              page-break-inside: avoid;
            }
            .info-header {
              font-size: 6px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 0;
              border-bottom: 1px solid #000;
              padding-bottom: 0;
            }
            .info-item {
              margin-bottom: 0;
              font-size: 5px;
              color: #000 !important;
              line-height: 1.3;
            }
            .info-label {
              font-weight: bold;
              color: #000 !important;
              min-width: 80px;
              display: inline-block;
            }
            .summary-table-container {
              border: 1px solid #000;
              margin-bottom: 1px;
              background: #fff !important;
              page-break-inside: avoid;
            }
            .table-container {
              border: 1px solid #000;
              margin-bottom: 1px;
              background: #fff !important;
              page-break-inside: auto !important;
            }
            .modern-table, .modern-orders-table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
            }
            .modern-table th, .modern-orders-table th {
              background: #fff !important;
              color: #000 !important;
              padding: 5px 3px;
              border: 1px solid #000;
              font-weight: bold;
              font-size: 10px;
              text-align: center;
            }
            .modern-table td, .modern-orders-table td {
              padding: 1px 1px;
              border: 1px solid #000;
              color: #000 !important;
              font-size: 10px;
              text-align: center;
              page-break-inside: avoid;
            }
            .modern-orders-table tbody tr, .modern-table tbody tr {
              page-break-inside: avoid !important;
            }
            .stat-badge {
              background: #fff !important;
              color: #000 !important;
              border: 1px solid #000 !important;
              padding: 2px 4px;
              font-size: 10px;
              font-weight: bold;
            }
            .amount, .total-amount {
              font-weight: bold;
              color: #000 !important;
              font-size: 10px;
            }
            .signature-section {
              margin-top: 2px;
              page-break-inside: avoid;
            }
            .signature-title {
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 1px;
              font-size: 6px;
              text-align: center;
            }
            .signature-line {
              height: 15px;
              border-bottom: 1px solid #000;
              margin-bottom: 1px;
              background: #fff !important;
            }
            .totals-footer {
              margin-top: 2px;
              padding: 2px;
              border: 1px solid #000;
              background: #fff !important;
              page-break-inside: avoid;
            }
            .totals-grid {
              display: flex;
              justify-content: space-between;
              align-items: center;
              gap: 1px;
            }
            .total-item {
              display: flex;
              flex-direction: column;
              align-items: center;
              padding: 1px;
              background: #fff !important;
              color: #000 !important;
              border-radius: 0;
              box-shadow: none;
            }
            .total-item.highlight {
              background: #eaeaea !important;
              color: #000 !important;
              border: 1px solid #000;
            }
            .total-label {
              font-size: 10px;
              font-weight: bold;
              color: #000 !important;
              margin-bottom: 2px;
            }
            .total-value {
              font-size: 12px;
              font-weight: bold;
              color: #000 !important;
            }
            /* --- PAGE BREAK IMPROVEMENTS --- */
            .section-title, .section-header {
              page-break-after: avoid !important;
            }
            .company-info-section, .summary-section, .signature-section, .totals-footer {
              page-break-inside: avoid !important;
            }
            .reason-main {
              page-break-inside: avoid !important;
            }
            /* Remove card body padding that causes extra space */
            .card-body {
              padding: 0 !important;
            }
            .signature-section .row {
              margin-bottom: 0 !important;
            }
            * {
              transition: none !important;
              animation: none !important;
              transform: none !important;
            }
          }
        </style>
      `

        document.head.insertAdjacentHTML('beforeend', printCSS)
        window.print()
        setTimeout(() => {
          const printStyleElement = document.getElementById('print-styles-bw')
          if (printStyleElement) {
            printStyleElement.remove()
          }
          document.title = originalTitle
          if (faviconEl && originalFaviconHref) {
            faviconEl.setAttribute('href', originalFaviconHref)
          }
          this.visualMode = previousMode
        }, 1000)
      })
    },
    setVisualMode(mode) {
      this.visualMode = mode
    },
  },
  setup() {
    return {}
  },
}
</script>

<style lang="scss" scoped>
// Brand Colors - MZ Logistic (Teal/Navy Theme)
$brand-primary: #0A2634;    // Dark teal/navy from branding
$brand-secondary: #1D1D1B;  // Dark charcoal
$brand-accent: #1a4a5e;     // Lighter teal accent
$brand-light: #e8f4f8;      // Light teal
$brand-dark: #061a22;       // Very dark teal
$accent-red: #e0111a;
$accent-red-dark: #b0151a;

.return-voucher-wrapper {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  min-height: 100vh;
  font-size: 16px;
}

.top-action-bar {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-left: 4px solid $accent-red;
}

.page-title {
  color: #1f1f1f;
  font-weight: 700;
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  color: #718096;
  font-size: 0.875rem;
  margin: 0;
}

.action-buttons-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 1rem;
  }
}

.view-mode-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .view-mode-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: $brand-secondary;
  }

  .btn-group {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 6px;

    .btn {
      font-size: 0.875rem;
      font-weight: 500;
      padding: 0.5rem 1rem;
      border: 1px solid #dee2e6;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-1px);
      }

      &.btn-danger {
        background-color: $accent-red;
        border-color: $accent-red;
        color: white;
      }

      &.btn-dark {
        background-color: $brand-secondary;
        border-color: $brand-secondary;
        color: white;
      }
    }
  }
}

.action-btn-top {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  border-color: rgba(234, 84, 85, 0.65);
  color: $accent-red-dark;

  &:hover {
    background-color: rgba(234, 84, 85, 0.08);
    border-color: rgba(234, 84, 85, 0.75);
    color: $accent-red-dark;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 38, 52, 0.2);
  }

  &.dropdown .btn {
    border-color: rgba(234, 84, 85, 0.65);
    color: $accent-red-dark;
    background-color: transparent;

    &:hover, &:focus {
      background-color: rgba(234, 84, 85, 0.08) !important;
      border-color: rgba(234, 84, 85, 0.75) !important;
      color: $accent-red-dark !important;
      box-shadow: 0 4px 12px rgba(10, 38, 52, 0.2) !important;
    }

    &:active, &.active, &.show {
      background-color: rgba(234, 84, 85, 0.12) !important;
      border-color: rgba(234, 84, 85, 0.75) !important;
      color: $accent-red-dark !important;
      box-shadow: 0 4px 12px rgba(10, 38, 52, 0.25) !important;
    }
  }

  .dropdown-menu {
    border-radius: 8px;
    border: 1px solid rgba(10, 38, 52, 0.2);
    box-shadow: 0 8px 25px rgba(10, 38, 52, 0.15);
    margin-top: 0.5rem;

    .dropdown-item {
      padding: 0.75rem 1rem;
      font-weight: 500;
      color: $brand-secondary;
      transition: all 0.2s ease;
      border-radius: 6px;
      margin: 0.25rem;

      &:hover, &:focus {
        background-color: rgba(10, 38, 52, 0.08) !important;
        color: $accent-red-dark !important;
        transform: translateX(2px);
      }

      .feather-icon {
        color: $accent-red-dark;
        transition: color 0.2s ease;
      }
    }
  }
}

.quick-stats {
  @media (max-width: 768px) {
    margin-left: 0 !important;
    margin-top: 0.5rem;
  }
}

.stat-item-inline {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  margin-left: 0.5rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;

  svg {
    margin-right: 0.25rem;
  }

  &.info {
    background: rgba(10, 38, 52, 0.1);
    color: $brand-primary;
  }

  &.danger {
    background: rgba(220, 53, 69, 0.1);
    color: #dc3545;
  }

  &.primary {
    background: rgba(10, 38, 52, 0.1);
    color: $brand-primary;
  }

  &.success {
    background: rgba(40, 199, 111, 0.1);
    color: #28c76f;
  }

  &.warning {
    background: rgba(255, 159, 67, 0.1);
    color: #ff9f43;
  }
}

.modern-alert {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 12px rgba(10, 38, 52, 0.15);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
}

.loading-text {
  color: #6c757d;
  font-size: 1.1rem;
}

.voucher-content {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.voucher-card {
  border-radius: 16px;
  border: none;
  overflow: hidden;
  background: #fff;
}

.voucher-header {
  padding: 2rem;
  background: linear-gradient(135deg, rgba(234, 84, 85, 0.10) 0%, rgba(234, 84, 85, 0.10) 100%);
  color: #121212;
  margin: -1.25rem -1.25rem 2rem -1.25rem;
  border-left: 6px solid $accent-red;
}

.company-logo {
  .logo-img {
    max-width: 200px;
    height: auto;
    background: transparent;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
  }
}

.voucher-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.voucher-number {
  color: $accent-red-dark;
}

.voucher-date {
  font-size: 1rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.company-info-section {
  margin-bottom: 2rem;
}

.info-card {
  background: #f7fafc;
  border-radius: 12px;
  padding: 1.5rem;
  height: 100%;
  border-left: 4px solid $accent-red;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
}

.info-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: $brand-secondary;
  font-weight: 600;
}

.info-content {
  .info-item {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-subtitle {
    display: block;
    font-size: 0.875rem;
    color: #718096;
    font-style: italic;
  }

  .info-label {
    font-weight: 600;
    color: $brand-secondary;
    display: inline-block;
    min-width: 120px;
  }
}

.summary-section, .orders-section {
  margin-bottom: 2rem;
}

.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: $brand-secondary;
  margin-bottom: 1rem;
}

.section-header {
  margin-bottom: 1rem;
}

.summary-table-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.modern-table {
  width: 100%;
  margin-bottom: 0;

  thead {
    background: linear-gradient(135deg, #000000 0%, #111111 100%);

    th {
      padding: 1rem;
      font-weight: 600;
      color: #ffffff;
      border: none;
      text-align: center;
      font-size: 0.875rem;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e2e8f0;

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 1rem;
      text-align: center;
      vertical-align: middle;
      border: none;
    }
  }
}

.stat-badge {
  font-size: 1rem;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
}

.amount {
  font-weight: 600;
  color: $brand-secondary;

  &.danger {
    color: #dc3545;
  }
}

.total-amount {
  font-size: 1.125rem;
  color: $brand-primary;
  font-weight: 700;
}

.table-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.modern-orders-table {
  margin-bottom: 0;

  thead th {
    background: #f7fafc;
    border-bottom: 2px solid #e2e8f0;
    font-weight: 600;
    color: $brand-secondary;
    padding: 1rem 0.75rem;
  }

  tbody {
    tr {
      transition: all 0.2s ease;

      &:hover {
        background-color: #f7fafc;
      }
    }

    td {
      padding: 0.75rem;
      border-color: #edf2f7;
      vertical-align: middle;
    }
  }
}

.order-id-cell {
  .order-number {
    color: $brand-primary;
    font-weight: 600;
    margin: 0;
  }

  .exchange-badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    margin-top: 0.25rem;
  }
}

.supplier-cell,
.customer-cell {
  display: flex;
  align-items: center;

  .supplier-name,
  .customer-name {
    font-weight: 500;
  }
}

.address-cell {
  .address-item {
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.phone-cell {
  .phone-item {
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.price-cell {
  text-align: right;

  .price-amount {
    font-weight: 600;
    color: #28c76f;
  }
}

.return-price-cell {
  text-align: right;

  .return-price-amount {
    font-weight: 600;
    color: #dc3545;
  }
}

.return-reasons-cell {
  max-width: 350px;
  min-width: 280px;
}

.reason-item {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.reason-main {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem;
  border-left: 3px solid $brand-primary;
}

.reason-text {
  margin-bottom: 0.5rem;
}

.reason-title {
  display: block;
  color: $brand-primary;
  font-weight: 700;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.reason-note {
  display: flex;
  align-items: flex-start;
  font-size: 0.8rem;
  color: #6c757d;
  font-style: italic;
  margin-top: 0.25rem;

  svg {
    margin-top: 0.125rem;
    flex-shrink: 0;
  }
}

.reason-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.reason-date {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #495057;

  &.scheduled {
    color: #007bff;
  }

  svg {
    flex-shrink: 0;
  }
}

.date-label {
  font-weight: 600;
  margin-right: 0.25rem;
}

.date-value {
  font-weight: 500;
}

.reason-options {
  margin-top: 0.5rem;
}

.options-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.option-badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

.reason-divider {
  height: 1px;
  background: #dee2e6;
  margin: 0.75rem 0;
}

.no-reason {
  display: flex;
  align-items: center;
  font-style: italic;
  color: #6c757d;
  font-size: 0.875rem;
}

.signature-section {
  margin-top: 3rem;
  padding: 2rem 0;
  border-top: 2px solid #e9ecef;
}

.signature-box {
  text-align: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
}

.signature-title {
  color: $brand-secondary;
  font-weight: 600;
  margin-bottom: 1rem;
}

.signature-line {
  width: 100%;
  height: 80px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
}

.totals-footer {
  margin-top: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border: 1px solid #dee2e6;
}

.totals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.total-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  &.highlight {
    background: linear-gradient(135deg, $brand-primary 0%, $brand-accent 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(10, 38, 52, 0.3);
  }

  .total-label {
    font-weight: 600;
    color: inherit;
  }

  .total-value {
    font-weight: 700;
    font-size: 1.125rem;

    &.primary {
      color: $brand-primary;
    }

    &.danger {
      color: #dc3545;
    }

    .highlight & {
      color: white;
    }
  }
}

// Black & White Mode Styling
.voucher-card--bw {
  .voucher-header {
    background: #fff !important;
    color: #000 !important;
    border: 2px solid #000;
    border-radius: 0;

    .voucher-title,
    .voucher-date {
      color: #000 !important;
    }

    .voucher-number {
      color: #000 !important;
      text-decoration: underline;
    }
  }

  .company-logo .logo-img {
    background: #fff !important;
    padding: 8px 12px;
    border: 2px solid #000;
    border-radius: 4px;
    box-shadow: none !important;
  }

  .info-card {
    background: #fff !important;
    border: 1px solid #000;
    border-left: 3px solid #000 !important;
  }

  .section-title {
    color: #000 !important;
    border-bottom: 2px solid #000;
    padding-bottom: 0.5rem;
  }

  .summary-table-container {
    border: 2px solid #000;
    box-shadow: none !important;
  }

  .modern-table thead {
    background: #f5f5f5 !important;

    th {
      color: #000 !important;
      border: 1px solid #000;
    }
  }

  .modern-table tbody {
    td {
      border: 1px solid #000;
    }
  }

  .table-container {
    border: 2px solid #000;
    box-shadow: none !important;
  }

  .modern-orders-table thead th {
    background: #f5f5f5 !important;
    color: #000 !important;
    border: 1px solid #000;
  }

  .modern-orders-table tbody td {
    border: 1px solid #000;
  }

  .stat-badge,
  .badge {
    background: #fff !important;
    color: #000 !important;
    border: 1px solid #000 !important;
  }

  .amount {
    color: #000 !important;

    &.danger,
    &.total-amount {
      color: #000 !important;
      font-weight: 700;
    }
  }

  .reason-main {
    background: #fff !important;
    border: 1px solid #000;
    border-left: 3px solid #000 !important;
  }

  .reason-title {
    color: #000 !important;
  }

  .signature-section {
    border-top: 2px solid #000;
  }

  .signature-line {
    border: 2px solid #000;
    background: #fff !important;
  }

  .totals-footer {
    background: #fff !important;
    border: 2px solid #000;
  }

  .total-item {
    background: #fff !important;
    border: 1px solid #000;

    &.highlight {
      background: #f0f0f0 !important;
      color: #000 !important;
      border: 2px solid #000 !important;

      .total-value {
        color: #000 !important;
      }
    }

    .total-value {
      &.primary,
      &.danger {
        color: #000 !important;
      }
    }
  }

  .order-number {
    color: #000 !important;
  }

  .price-cell .price-amount {
    color: #000 !important;
  }

  .return-price-cell .return-price-amount {
    color: #000 !important;
  }

  .feather-icon {
    color: #000 !important;
  }
}

// Color Mode Enhanced Styling
.voucher-card--color {
  .voucher-header {
    background: linear-gradient(135deg, rgba(234, 84, 85, 0.10) 0%, rgba(234, 84, 85, 0.10) 100%);
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 300px;
      height: 300px;
      background: rgba(234, 84, 85, 0.08);
      border-radius: 50%;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -30%;
      left: -5%;
      width: 200px;
      height: 200px;
      background: rgba(234, 84, 85, 0.06);
      border-radius: 50%;
    }
  }

  .info-card {
    transition: all 0.3s ease;
    border-left: 4px solid $accent-red;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }
  }

  .modern-table thead {
    background: linear-gradient(135deg, #000000 0%, #111111 100%);
  }

  .stat-badge {
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  .reason-main {
    transition: all 0.2s ease;

    &:hover {
      transform: translateX(3px);
      box-shadow: 0 4px 12px rgba(10, 38, 52, 0.1);
    }
  }

  .total-item {
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    }

    &.highlight {
      background: linear-gradient(135deg, $brand-primary 0%, $brand-accent 100%);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left 0.5s ease;
      }

      &:hover::before {
        left: 100%;
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .return-reasons-cell {
    max-width: 250px;
    min-width: 200px;
  }

  .reason-main {
    padding: 0.5rem;
  }

  .reason-title {
    font-size: 0.8rem;
  }

  .reason-meta {
    .reason-date {
      font-size: 0.7rem;
    }
  }

  .options-list {
    .option-badge {
      font-size: 0.65rem;
      padding: 0.2rem 0.4rem;
    }
  }

  .action-buttons-top {
    gap: 0.5rem;
  }

  .view-mode-toggle {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
